import i18nFactory from '@yandex-int/i18n';
import classNames from 'classnames';
import { TextInput, Tumbler } from 'lego-on-react';
import * as React from 'react';

import { Bem } from '../../service/Bem/Bem';
import {
    filterSegmentsBySearch,
    formatName,
    Rate,
    Section,
    Segment,
    TreeSection
} from '../../service/Experiments/Experiments';
import { Expander } from '../Expander/Expander';
import { ExSegmentRate } from '../ExSegmentRate/ExSegmentRate';
import { MarkedText } from '../MarkedText/MarkedText';
import { AdditionalTumblerTone, TumblerDecorator } from '../TumblerDecorator/TumblerDecorator';

import './ExSegmentsRates.css';
import * as keyset from './ExSegmentsRates.i18n';

const i18n = i18nFactory(keyset);

interface ExSegmentsRatesProps {
    treeData: Record<string, Section>;
    treeOrder: Array<TreeSection>;
    isShowOnlySelected: boolean;
    onActiveChange: () => void;
    onChange: (segmentId: Segment['segment_id'], data: Rate) => void;
    isActive: boolean;
    cls?: string;
    view?: 'classic' | 'default';
}

interface ExSegmentsRatesState {
    search: string;
    opened: Set<Section['section_id']>;
}

export class ExSegmentsRates extends React.Component<ExSegmentsRatesProps, ExSegmentsRatesState> {
    public static defaltProps: Partial<ExSegmentsRatesProps> = {
        view: 'default'
    };

    constructor(props: ExSegmentsRatesProps) {
        super(props);

        this.state = {
            search: '',
            opened: this.calcOpened(props.treeData, props.treeOrder)
        };
    }

    render() {
        const { cls, isActive, onActiveChange, treeData, treeOrder } = this.props;
        const { search } = this.state;

        const searchedTree = filterSegmentsBySearch(treeData, treeOrder, search);

        return (
            <div className={classNames(Bem.cls('ex-segments-rates'), cls)}>
                <div className={Bem.cls('ex-segments-rates', 'title')}>
                    <TumblerDecorator
                        cls={Bem.cls('ex-segments-rates', 'switcher')}
                        theme="normal"
                        view={'default'}
                        size={'xs'}
                        tone={AdditionalTumblerTone.direct}
                        checked={isActive}
                        onChange={onActiveChange}
                    >
                        <Tumbler.Off id="random-id-for-ts" text={i18n('title')} side="left" />
                    </TumblerDecorator>
                </div>
                <div className={Bem.cls('ex-segments-rates', 'window')}>
                    <div className={Bem.cls('ex-segments-rates', 'search')}>
                        <TextInput
                            theme="normal"
                            size="s"
                            view="classic"
                            placeholder={i18n('search-placeholder')}
                            cls={Bem.cls('ex-segments-rates', 'search-input')}
                            text={search}
                            disabled={!isActive}
                            onChange={this.onSearchChange}
                            hasClear={true}
                        />
                    </div>
                    <div
                        className={classNames(
                            Bem.cls('ex-segments-rates', 'sections'),
                            Bem.cls('ex-segments-rates', 'sections', {
                                disabled: !isActive
                            })
                        )}
                    >
                        {searchedTree.length ? this.renderSections(treeData, searchedTree) : this.getNotFound()}
                    </div>
                </div>
            </div>
        );
    }

    /**
     * Формирование текста, который показывается, если нет отображаемых экспериментов
     * @return {JSX.Element}
     */
    private getNotFound = (): JSX.Element => {
        const { isShowOnlySelected } = this.props;

        return (
            <p className={Bem.cls('ex-segments-rates', 'not-found')}>
                {isShowOnlySelected ? i18n('not-selected') : i18n('not-found')}
            </p>
        );
    };

    /**
     * Формирование списка экспериментов
     * @return {JSX.Element}
     */
    private renderSections = (treeData: Record<string, Section>, tree: Array<TreeSection>): (JSX.Element | '')[] => {
        const { isShowOnlySelected } = this.props;
        const { search, opened } = this.state;

        return tree.map(sect => {
            const section = treeData[sect.id];

            return (
                <Expander
                    key={section.section_id}
                    isOpen={
                        isShowOnlySelected || search.length ? sect.segments.length > 0 : opened.has(section.section_id)
                    }
                    onClick={this.onTreeItemClick}
                    eventData={section}
                    isClickable={true}
                    item={
                        <div
                            className={classNames(
                                Bem.cls('ex-segments-rates', 'section'),
                                section.archive &&
                                    Bem.cls('ex-segments-rates', 'section', {
                                        archive: true
                                    })
                            )}
                        >
                            <MarkedText text={formatName(section.section_name, !!section.archive)} search={search} />
                        </div>
                    }
                >
                    {this.renderSegments(treeData, sect)}
                </Expander>
            );
        });
    };

    /**
     * Формирование списка сегментов
     * @return {JSX.Element}
     */
    private renderSegments = (treeData: Record<string, Section>, treeSection: TreeSection): (JSX.Element | '')[] => {
        const { search } = this.state;
        const section = treeData[treeSection.id];

        return treeSection.segments.map(seg => {
            const segment = section.segments[seg.id];

            return (
                <div
                    key={segment.segment_id}
                    className={classNames(
                        Bem.cls('ex-segments-rates', 'segment'),
                        section.archive &&
                            Bem.cls('ex-segments-rates', 'segment', {
                                archive: true
                            })
                    )}
                >
                    <ExSegmentRate
                        key={`${segment.segment_id}-rate`}
                        name={
                            <MarkedText
                                text={`${segment.segment_name} - ${Number(segment.percent)}%`}
                                search={search}
                            />
                        }
                        segmentId={segment.segment_id}
                        archived={!!segment.archive}
                        rate={segment.rate}
                        onChange={this.onRateChange}
                    />
                </div>
            );
        });
    };

    /**
     * Обработчик открытия дерева
     * @param {Object} data
     */
    private onTreeItemClick = (data: { section_id: number }) => {
        const { opened } = this.state;

        const newOpened = new Set(opened);

        newOpened.has(data.section_id) ? newOpened.delete(data.section_id) : newOpened.add(data.section_id);

        this.setState({
            opened: newOpened
        });
    };

    /**
     * Обработчик изменения поля поиска
     * @param {string} value - новое значение
     */
    private onSearchChange = (value: string): void => {
        this.setState({
            search: value
        });
    };

    private onRateChange = (segmentId: Segment['segment_id'], data: Rate) => {
        this.props.onChange(segmentId, data);
    };

    /**
     * Вычислание раскрытых экспериментов в дереве
     */
    private calcOpened = (treeData: Record<string, Section>, tree: Array<TreeSection>): Set<number> => {
        const opened = new Set<number>();

        tree.forEach(s => {
            if (s.segments.some(seg => !!treeData[s.id].segments[seg.id].rate)) {
                opened.add(s.id);
            }
        });

        return opened;
    };
}
